import { trackEvent } from '@/helpers/GTM/GTMHelper';
import {
  EventLabel,
  InteractionEvent,
  InteractionGoal,
  PageType,
  SortingOptionEventLabel,
} from '@/helpers/GTM/types';
import { type Agency, ListingType, ListingTypeForTracking, SortingOption } from '@/types';
import { type GeoLocation } from '@smg-real-estate/geo-libs';

interface AgencyDirectoryPageViewTrackingParams {
  agencies: Agency[];
  filterLocations: string[];
  totalAgenciesCount: number;
  pageCount: number;
  currentPage: number;
  language: string;
  listingType: ListingType;
  sortingOption: SortingOption;
}

export const getSortingLabel = (sortingOption: SortingOption): SortingOptionEventLabel => {
  const sortingLabels: Record<SortingOption, SortingOptionEventLabel> = {
    [SortingOption.ALPHABETICALLY]: SortingOptionEventLabel.ALPHABET_ASC,
    [SortingOption.PROPERTIES_FOR_RENT]: SortingOptionEventLabel.TOTALRENTADS_DESC,
    [SortingOption.PROPERTIES_FOR_SALE]: SortingOptionEventLabel.TOTALSALEADS_DESC,
    [SortingOption.DEFAULT]: SortingOptionEventLabel.COMPLETENESS_DESC,
  };

  return sortingLabels[sortingOption];
};

export const trackAgencyDirectoryPageView = ({
  agencies,
  filterLocations,
  totalAgenciesCount,
  pageCount,
  currentPage,
  language,
  listingType,
  sortingOption,
}: AgencyDirectoryPageViewTrackingParams): void => {
  trackEvent(
    InteractionEvent.LOAD,
    buildAgencyDirectoryPageViewPayload({
      agencies,
      filterLocations,
      totalAgenciesCount,
      pageCount,
      currentPage,
      language,
      listingType,
      sortingOption,
    }),
  );
};

export const buildAgencyDirectoryPageViewPayload = ({
  agencies,
  filterLocations,
  totalAgenciesCount,
  pageCount,
  currentPage,
  language,
  listingType,
  sortingOption,
}: AgencyDirectoryPageViewTrackingParams) => {
  return {
    event: InteractionEvent.LOAD,
    h_page_type_110: PageType.AGENCY_DIRECTORY,
    h_page_subtype_112: ListingTypeForTracking[listingType],
    h_content_language_116: language,
    h_search_term_geo_080:
      filterLocations.length > 0
        ? filterLocations.map((item) => (item.startsWith('geo-') ? item : `geo-${item}`)).join(',')
        : undefined,
    h_number_of_search_terms_entered_278: filterLocations.length,
    h_search_offer_type_083: ListingTypeForTracking[listingType],
    h_list_name_086: 'agency_directory_search_result',
    h_srp_items_loaded_078: agencies.map((agency: Agency) => agency.customerId),
    h_search_results_loaded_279: agencies.length,
    h_search_results_total_280: totalAgenciesCount,
    h_search_results_pages_all_287: pageCount,
    h_search_results_page_current_288: currentPage,
    h_search_sorted_details_073: getSortingLabel(sortingOption),
  };
};

interface SearchButtonSubmitTrackingParams {
  selectedLocations: GeoLocation[];
  listingType: ListingType;
}
export const trackSearchButtonSubmit = ({
  selectedLocations,
  listingType,
}: SearchButtonSubmitTrackingParams): void => {
  trackEvent(InteractionEvent.SEARCH_SUBMIT, {
    event: InteractionEvent.SEARCH_SUBMIT,
    h_event_category_006: 'search_submit',
    h_event_action_007:
      selectedLocations.length > 0 ? 'agency_directory_changeSearch' : 'agency_directory_search',
    h_event_label_008: selectedLocations.map((location) => location.id).join(','),
    h_search_term_geo_080: selectedLocations.map((location) => location.id).join(','),
    h_search_offer_type_083: ListingTypeForTracking[listingType],
    h_number_of_search_terms_entered_278: selectedLocations.length,
  });
};

export const trackFeaturedAgencyCardClick = ({ customerId }: Agency, position: number): void => {
  trackEvent(InteractionEvent.LIST_CLICKTHROUGH, {
    event: InteractionEvent.LIST_CLICKTHROUGH,
    h_event_category_006: 'list_clickthrough',
    h_event_action_007: 'agency_recommendation',
    h_event_label_008: customerId,
    h_list_name_086: 'agency_recommendation',
    h_list_item_clicked_id_085: customerId,
    h_position_on_list_overall_283: position,
  });
};

interface SearchResultsAgencyCardClickTrackingParams {
  clickedSearchResultsAgencyCard: Agency;
  position: number;
}
export const trackSearchResultsAgencyCardClick = ({
  clickedSearchResultsAgencyCard,
  position,
}: SearchResultsAgencyCardClickTrackingParams): void => {
  trackEvent(InteractionEvent.LIST_CLICKTHROUGH, {
    event: InteractionEvent.LIST_CLICKTHROUGH,
    h_event_category_006: 'list_clickthrough',
    h_event_action_007: 'agency_directory_search_result',
    h_event_label_008: clickedSearchResultsAgencyCard.customerId,
    h_list_name_086: 'agency_directory_search_result',
    h_list_item_clicked_id_085: clickedSearchResultsAgencyCard.customerId,
    h_position_on_list_overall_283: position,
  });
};

export const trackPaginationClick = (operation: string): void => {
  trackEvent(InteractionEvent.GENERIC_INTERACTION, {
    event: 'generic_interaction',
    h_event_category_006: 'generic_interaction',
    h_event_action_007: 'sort',
    h_event_label_008: operation,
  });
};

export const trackFavouritesNavigation = (language: string): void => {
  trackEvent(InteractionEvent.HEADER_INTERACTION, {
    event: InteractionEvent.HEADER_INTERACTION,
    h_event_category_006: InteractionEvent.HEADER_INTERACTION,
    h_event_action_007: InteractionGoal.OPEN_FAVOURITES,
    h_event_label_008: EventLabel.TAB_NAVIGATION,
    h_content_language_116: language,
  });
};

export const trackSortingOptionChange = (sortingOption: SortingOption): void => {
  trackEvent(InteractionEvent.SEARCH_SORT, {
    event: InteractionEvent.SEARCH_SORT,
    h_event_category_006: 'search_sort',
    h_event_action_007: 'sort',
    h_event_label_008: getSortingLabel(sortingOption),
  });
};
